import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion as Motion } from "framer-motion";
import api from "@/utils/axios";
import { useCall } from "@/app/call-context-state";

import avatarPng from "@/assets/UI/avatar.png";

import audioCallIcon from "@/assets/UI/audioCall.webp";
import videoCallIcon from "@/assets/UI/videoCall.webp";

import incomingCallMini from "@/assets/UI/incomingCall.webp";
import outgoingCallMini from "@/assets/UI/outgoingCall.webp";

import cls from "./CallDetails.module.scss";

const PAGE_SIZE = 12;

const fadeIn = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

const formatCallDate = (value) => {
  if (!value) return "--";
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleString();
};

const formatDuration = (durationSeconds) => {
  if (!Number.isFinite(durationSeconds)) return "--";
  const totalSeconds = Math.max(0, Math.floor(durationSeconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts = [
    hours > 0 ? String(hours).padStart(2, "0") : null,
    String(minutes).padStart(2, "0"),
    String(seconds).padStart(2, "0"),
  ].filter(Boolean);

  return parts.join(":");
};

const normalizeDirection = (call) => {
  const raw =
    call.direction ||
    call.callDirection ||
    call.typeDirection ||
    call.displayStatus ||
    call.status ||
    "";

  const v = String(raw).toLowerCase();

  if (v.includes("out")) return "outgoing";
  if (v.includes("in")) return "incoming";

  if (Number(call.durationSeconds) === 0) return "incoming";
  return "incoming";
};

const CallDetails = () => {
  const [calls, setCalls] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const { startCall, activeCall, isConnecting } = useCall();

  const fetchCalls = useCallback(async (pageToLoad, append = false) => {
    try {
      if (pageToLoad === 1) setLoading(true);
      else setLoadingMore(true);

      setError("");

      const { data } = await api.get("/v1/calls/history", {
        params: { page: pageToLoad, limit: PAGE_SIZE },
      });

      const list = data?.calls || [];
      setCalls((prev) => (append ? [...prev, ...list] : list));
      setHasMore(Boolean(data?.hasMore));
      setPage(pageToLoad);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Unable to load call history.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchCalls(1, false);
  }, [fetchCalls]);

  const handleLoadMore = () => {
    if (!hasMore || loadingMore) return;
    fetchCalls(page + 1, true);
  };

  const canCall = useMemo(
    () => !activeCall && !isConnecting,
    [activeCall, isConnecting],
  );

  const handleStartCall = async (calleeId, callType) => {
    if (!calleeId || !canCall) return;
    await startCall({ calleeId, callType });
  };

  return (
    <div className={cls.page}>
      <div className={cls.header}>
        <h1>Call Details</h1>
        <p>Recent call history with your contacts.</p>
      </div>

      <div className={cls.panel}>
        {loading && <div className={cls.state}>Loading call history…</div>}

        {!loading && error && (
          <div className={cls.stateError} role="alert">
            {error}
          </div>
        )}

        {!loading && !error && calls.length === 0 && (
          <div className={cls.state}>No recent calls yet.</div>
        )}

        <Motion.ul
          className={cls.list}
          initial="hidden"
          animate="show"
          variants={{ show: { transition: { staggerChildren: 0.04 } } }}
        >
          {calls.map((call) => {
            const participant = call.participant || {};
            const username = participant.username || "Unknown";
            const avatarSrc = participant.avatarUrl || avatarPng;

            const direction = normalizeDirection(call);
            const directionMini =
              direction === "outgoing" ? outgoingCallMini : incomingCallMini;

            const callType = call.callType === "video" ? "video" : "audio";
            const callTypeBadgeIcon =
              callType === "video" ? videoCallIcon : audioCallIcon;

            const durationSeconds = Number(call.durationSeconds || 0);
            const isMissed = durationSeconds === 0;

            const canCallUser = canCall && Boolean(participant.id);
            const isAdminParticipant = participant.role === "admin";

            return (
              <Motion.li key={call.id} className={cls.item} variants={fadeIn}>
                <div
                  className={`${cls.avatarWrap} ${
                    cls[`ring--${direction}`] || ""
                  }`}
                >
                  <img src={avatarSrc} alt="" className={cls.avatar} />

                  {/* callType badge must sit IN FRONT of ring */}
                  <div className={cls.callTypeBadge} aria-hidden="true">
                    <img src={callTypeBadgeIcon} alt="" />
                  </div>
                </div>

                <div className={cls.content}>
                  <div className={cls.rowTop}>
                    <div className={cls.userLine}>
                      <span className={cls.username}>{username}</span>

                      {/* round 3D chip for incoming/outgoing */}
                      <span className={cls.dirChip} aria-hidden="true">
                        <img
                          className={cls.dirIcon}
                          src={directionMini}
                          alt=""
                        />
                      </span>
                    </div>
                  </div>

                  <div className={cls.durationLine}>
                    {isMissed ? (
                      <span className={cls.missed}>Missed call</span>
                    ) : (
                      <span className={cls.durationText}>
                        {formatDuration(durationSeconds)}
                      </span>
                    )}
                  </div>

                  <div className={cls.dateLine}>
                    {formatCallDate(call.createdAt)}
                  </div>
                </div>

                {!isAdminParticipant && (
                  <div className={cls.actions}>
                    <button
                      type="button"
                      className={cls.actionBtn}
                      onClick={() => handleStartCall(participant.id, "audio")}
                      disabled={!canCallUser}
                      aria-label={`Audio call ${username}`}
                      title="Audio call"
                    >
                      <span className={cls.actionIconChip} aria-hidden="true">
                        <img
                          className={cls.actionIcon}
                          src={audioCallIcon}
                          alt=""
                        />
                      </span>
                    </button>

                    <button
                      type="button"
                      className={cls.actionBtn}
                      onClick={() => handleStartCall(participant.id, "video")}
                      disabled={!canCallUser}
                      aria-label={`Video call ${username}`}
                      title="Video call"
                    >
                      <span className={cls.actionIconChip} aria-hidden="true">
                        <img
                          className={cls.actionIcon}
                          src={videoCallIcon}
                          alt=""
                        />
                      </span>
                    </button>
                  </div>
                )}
              </Motion.li>
            );
          })}
        </Motion.ul>

        {hasMore && (
          <button
            type="button"
            className={cls.loadMore}
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        )}
      </div>
    </div>
  );
};

export default CallDetails;
